import { useEffect, useRef, useState } from "react";
import { Gift, Loader2, Trophy, Users, Clock, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function useCountdown(endIso) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endIso) return null;
  const end = new Date(endIso);
  const ms = end.getTime() - now.getTime();
  if (ms <= 0) return { ended: true, d: 0, h: 0, m: 0, s: 0 };
  const totalSec = Math.floor(ms / 1000);
  return {
    ended: false,
    d: Math.floor(totalSec / 86400),
    h: Math.floor((totalSec % 86400) / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

function StatusBadge({ status }) {
  if (status === "drawn")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <Trophy className="w-3 h-3" /> Gewinner gezogen
      </span>
    );
  if (status === "ended")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Clock className="w-3 h-3" /> Beendet · wartet auf Ziehung
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1D5BFF]/15 text-[#4DBFFF] border border-[#1D5BFF]/40">
      <Sparkles className="w-3 h-3" /> Aktiv
    </span>
  );
}

function CountdownDisplay({ endsAt }) {
  const c = useCountdown(endsAt);
  if (!c) return null;
  if (c.ended) return <span className="text-amber-400 font-mono text-sm font-bold">00d 00h 00m 00s</span>;
  return (
    <div className="grid grid-cols-4 gap-1.5 font-mono text-sm sm:text-base font-bold text-slate-900 tabular-nums" data-testid="countdown">
      <span className="bg-[#F5F8FD] border border-slate-200 rounded px-1.5 sm:px-2 py-1 text-center">{String(c.d).padStart(2, "0")}<span className="text-slate-500 ml-0.5 text-[9px] sm:text-[10px]">T</span></span>
      <span className="bg-[#F5F8FD] border border-slate-200 rounded px-1.5 sm:px-2 py-1 text-center">{String(c.h).padStart(2, "0")}<span className="text-slate-500 ml-0.5 text-[9px] sm:text-[10px]">h</span></span>
      <span className="bg-[#F5F8FD] border border-slate-200 rounded px-1.5 sm:px-2 py-1 text-center">{String(c.m).padStart(2, "0")}<span className="text-slate-500 ml-0.5 text-[9px] sm:text-[10px]">m</span></span>
      <span className="bg-[#F5F8FD] border border-slate-200 rounded px-1.5 sm:px-2 py-1 text-center">{String(c.s).padStart(2, "0")}<span className="text-slate-500 ml-0.5 text-[9px] sm:text-[10px]">s</span></span>
    </div>
  );
}

function GiveawayCard({ g, onEnter, onLeave, busy, onImageClick }) {
  return (
    <div
      data-testid={`giveaway-card-${g.id}`}
      className="bg-[#FFFFFF] border border-slate-200 rounded-2xl overflow-hidden hover:border-[#1D5BFF]/50 hover:shadow-[0_8px_40px_rgba(0,102,255,0.15)] transition-all flex flex-col"
    >
      {g.image_url && (
        <button
          type="button"
          onClick={onImageClick}
          className="w-full bg-[#F5F8FD] flex items-center justify-center overflow-hidden cursor-zoom-in group"
          style={{ minHeight: "180px", maxHeight: "420px" }}
          data-testid={`giveaway-image-${g.id}`}
        >
          <img
            src={fileUrl(g.image_url)}
            alt={g.title}
            loading="lazy"
            className="w-auto h-auto max-w-full object-contain transition-transform group-hover:scale-[1.02]"
            style={{ maxHeight: "420px" }}
          />
        </button>
      )}
      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-display text-base sm:text-lg font-black text-slate-900 leading-tight break-words min-w-0 flex-1">
            {g.title}
          </h3>
          <StatusBadge status={g.status} />
        </div>

        <div className="bg-gradient-to-br from-[#1D5BFF]/15 to-emerald-500/10 border border-[#1D5BFF]/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#4DBFFF] mb-0.5 flex items-center gap-1">
            <Gift className="w-3 h-3" /> Gewinn
          </div>
          <div className="text-slate-900 font-bold text-sm sm:text-base break-words">{g.prize}</div>
        </div>

        {g.description && (
          <p className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap break-words line-clamp-4">{g.description}</p>
        )}

        {g.status === "drawn" ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-3 min-w-0">
            {g.winner_avatar && (
              <img src={fileUrl(g.winner_avatar)} alt={g.winner_name} className="w-10 h-10 rounded-full object-cover border border-emerald-400/50 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                <Trophy className="w-3 h-3" /> Gewinner
              </div>
              <div className="font-bold text-slate-900 truncate">{g.winner_name}</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[11px] sm:text-xs text-slate-600 flex-wrap gap-1">
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-[#4DBFFF]" /> {g.entries_count} Teilnehmer
              </span>
              <span className="text-slate-500 truncate">endet {new Date(g.ends_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
            </div>
            <CountdownDisplay endsAt={g.ends_at} />
          </div>
        )}

        <div className="mt-auto pt-2">
          {g.status === "active" && !g.my_entry && (
            <button
              onClick={() => onEnter(g)}
              disabled={busy}
              data-testid={`enter-giveaway-${g.id}`}
              className="w-full bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-50 text-white font-semibold py-2.5 rounded-full transition-all active:scale-95 shadow-[0_8px_30px_rgba(0,102,255,0.35)] flex items-center justify-center gap-2"
            >
              <Gift className="w-4 h-4" /> Teilnehmen
            </button>
          )}
          {g.status === "active" && g.my_entry && (
            <button
              onClick={() => onLeave(g)}
              disabled={busy}
              data-testid={`leave-giveaway-${g.id}`}
              className="w-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-red-500/15 hover:border-red-500/40 hover:text-red-300 font-semibold py-2.5 rounded-full transition-all active:scale-95 flex items-center justify-center gap-2 text-xs sm:text-sm"
            >
              <Check className="w-4 h-4 shrink-0" />
              <span className="truncate">Du nimmst teil · zum Abmelden tippen</span>
            </button>
          )}
          {g.status === "ended" && (
            <div className="text-center text-xs text-amber-300 py-2 px-1">
              Verlosung beendet – Admin zieht in Kürze den Gewinner.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Verlosung() {
  const { user } = useAuth();
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/giveaways");
      setGiveaways(data);
    } catch (err) {
      console.error("load giveaways failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 10000); // refresh every 10s
    return () => clearInterval(timerRef.current);
  }, []);

  const enter = async (g) => {
    setBusyId(g.id);
    try {
      const { data } = await api.post(`/giveaways/${g.id}/enter`);
      setGiveaways((prev) => prev.map((x) => (x.id === g.id ? data : x)));
      toast.success("Viel Glück! 🍀 Du bist im Lostopf.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally {
      setBusyId(null);
    }
  };

  const leave = async (g) => {
    if (!window.confirm("Wirklich von der Verlosung abmelden?")) return;
    setBusyId(g.id);
    try {
      await api.delete(`/giveaways/${g.id}/enter`);
      load();
      toast.success("Abgemeldet.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally {
      setBusyId(null);
    }
  };

  const active = giveaways.filter((g) => g.status === "active" || g.status === "ended");
  const past = giveaways.filter((g) => g.status === "drawn");

  return (
    <div data-testid="verlosung-page" className="space-y-5 sm:space-y-6">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Gewinnspiele</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter flex items-center gap-2">
          Verlosung <Gift className="w-7 h-7 text-[#4DBFFF]" />
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">
          Mach mit, gewinne tolle Preise – nur für TSU-PARTNERS Fahrer.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Verlosungen…
        </div>
      ) : giveaways.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-10 text-center">
          <Gift className="w-12 h-12 text-[#1D5BFF] mx-auto mb-3" />
          <p className="text-slate-700 font-display text-lg">Aktuell keine Verlosung</p>
          <p className="text-sm text-slate-500 mt-1">Sobald eine neue Verlosung startet, siehst du sie hier.</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {active.map((g) => (
                <GiveawayCard
                  key={g.id}
                  g={g}
                  onEnter={enter}
                  onLeave={leave}
                  busy={busyId === g.id}
                  onImageClick={() => setLightboxImg(g)}
                />
              ))}
            </div>
          )}
          {past.length > 0 && (
            <div className="pt-2">
              <h2 className="text-sm uppercase tracking-[0.2em] text-slate-600 mb-3 flex items-center gap-1.5">
                <Trophy className="w-4 h-4" /> Vergangene Gewinner
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {past.map((g) => (
                  <GiveawayCard
                    key={g.id}
                    g={g}
                    onEnter={enter}
                    onLeave={leave}
                    busy={busyId === g.id}
                    onImageClick={() => setLightboxImg(g)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Lightbox: full image preview */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxImg(null)}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-900"
            data-testid="lightbox-close"
            aria-label="Schließen"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={fileUrl(lightboxImg.image_url)}
            alt={lightboxImg.title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl cursor-default"
          />
        </div>
      )}
    </div>
  );
}
